/**
 * The dashboard's alert sounds, synthesized on the spot instead of shipped as files. Each section has its own
 * voice, so the ear knows where an alert comes from before the eyes reach the screen, and within a section a
 * problem sounds like that voice going down instead of up:
 *
 * - `payin` — money arrived: a rising E major arpeggio (E6, G#6, B6) struck like a small bell, a soft E5 under
 *   the last note. The coin.
 * - `payout` / `payout_failed` — a transfer reached its destination, or did not: two dry, wooden notes (like a
 *   marimba), D5 up to G5 when it arrived, G5 down to D5 when it did not.
 * - `batch` / `batch_failed` — a payout batch finished: three quick ticks, one per row going out, then a bell
 *   up (G6) when every row went well, a round note down (A5) when some failed.
 * - `card` / `card_failed` — card activity: the two short beeps of a payment terminal approving, or one lower
 *   beep that sinks when the purchase was declined.
 *
 * All short (under two seconds with the echo), peaking near −9 dBFS: noticeable beside a till, never an
 * alarm.
 */
export type DashboardAlertSound =
  | "payin"
  | "payout"
  | "payout_failed"
  | "batch"
  | "batch_failed"
  | "card"
  | "card_failed";

type Timbre = "bell" | "round" | "wood";

interface ChimeNote {
  /** Seconds after the sound starts. */
  at: number;
  frequency: number;
  level: number;
  /** Seconds until the fundamental has faded out. */
  decay: number;
  timbre: Timbre;
  /** A frequency the note slides to while it fades: what makes a declined beep sink. */
  glideTo?: number;
}

const tick = (at: number): ChimeNote => ({
  at,
  frequency: 1046.5,
  level: 0.22,
  decay: 0.09,
  timbre: "round",
});

const SOUNDS: Record<DashboardAlertSound, readonly ChimeNote[]> = {
  payin: [
    { at: 0, frequency: 1318.51, level: 0.5, decay: 0.5, timbre: "bell" },
    { at: 0.085, frequency: 1661.22, level: 0.45, decay: 0.55, timbre: "bell" },
    { at: 0.17, frequency: 1975.53, level: 0.55, decay: 1.5, timbre: "bell" },
    { at: 0.17, frequency: 659.25, level: 0.18, decay: 1.3, timbre: "round" },
  ],
  payout: [
    { at: 0, frequency: 587.33, level: 0.5, decay: 0.3, timbre: "wood" },
    { at: 0.11, frequency: 783.99, level: 0.55, decay: 0.6, timbre: "wood" },
  ],
  payout_failed: [
    { at: 0, frequency: 783.99, level: 0.5, decay: 0.3, timbre: "wood" },
    { at: 0.13, frequency: 587.33, level: 0.55, decay: 0.75, timbre: "wood" },
  ],
  batch: [
    tick(0),
    tick(0.09),
    tick(0.18),
    { at: 0.3, frequency: 1567.98, level: 0.45, decay: 1.0, timbre: "bell" },
    { at: 0.3, frequency: 783.99, level: 0.15, decay: 0.8, timbre: "round" },
  ],
  batch_failed: [
    tick(0),
    tick(0.09),
    tick(0.18),
    { at: 0.3, frequency: 880, level: 0.45, decay: 0.7, timbre: "round" },
  ],
  card: [
    { at: 0, frequency: 1975.53, level: 0.55, decay: 0.1, timbre: "round" },
    { at: 0.14, frequency: 1975.53, level: 0.55, decay: 0.16, timbre: "round" },
  ],
  card_failed: [{ at: 0, frequency: 659.25, level: 0.8, decay: 0.55, timbre: "round", glideTo: 523.25 }],
};

/**
 * Partials as ratios of the fundamental. The bell's are inharmonic, so it rings like a bar and not like a
 * synth beep; the wood's upper partial (near four times) is what a marimba bar adds, gone almost at once; the
 * round tone keeps a faint octave only.
 */
const PARTIALS: Record<Timbre, readonly { ratio: number; level: number; decay: number }[]> = {
  bell: [
    { ratio: 1, level: 1, decay: 1 },
    { ratio: 2.76, level: 0.2, decay: 0.45 },
    { ratio: 5.4, level: 0.07, decay: 0.22 },
  ],
  round: [
    { ratio: 1, level: 1, decay: 1 },
    { ratio: 2, level: 0.12, decay: 0.5 },
  ],
  wood: [
    { ratio: 1, level: 1, decay: 1 },
    { ratio: 3.93, level: 0.22, decay: 0.18 },
    { ratio: 9.2, level: 0.05, decay: 0.08 },
  ],
};

const ATTACK_SECONDS: Record<Timbre, number> = { bell: 0.004, round: 0.012, wood: 0.003 };
const SILENCE = 0.0001;
const MASTER_LEVEL = 0.4;
const ECHO_SECONDS = 0.11;
const ECHO_FEEDBACK = 0.22;
const ECHO_LEVEL = 0.14;
/** A play request answered later than this is dropped: a sound long after the event would mislead. */
const MAX_START_DELAY_MS = 1500;
/** Sounds closer together than this would pile on each other; a burst of alerts plays once. */
const MIN_GAP_MS = 1800;

/** How long the longest sound lasts, echo included, so an offline render can size its buffer. */
export const DASHBOARD_ALERT_SOUND_SECONDS = 2.1;

function strike(context: BaseAudioContext, destination: AudioNode, note: ChimeNote, start: number) {
  for (const partial of PARTIALS[note.timbre]) {
    const begin = start + note.at;
    const end = begin + note.decay * partial.decay;
    const oscillator = context.createOscillator();
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(note.frequency * partial.ratio, begin);
    if (note.glideTo) {
      oscillator.frequency.exponentialRampToValueAtTime(note.glideTo * partial.ratio, end);
    }
    const envelope = context.createGain();
    envelope.gain.setValueAtTime(SILENCE, begin);
    envelope.gain.exponentialRampToValueAtTime(
      note.level * partial.level,
      begin + ATTACK_SECONDS[note.timbre],
    );
    envelope.gain.exponentialRampToValueAtTime(SILENCE, end);
    oscillator.connect(envelope).connect(destination);
    oscillator.start(begin);
    oscillator.stop(end + 0.05);
  }
}

/** Lays one sound on `context` from `start` (its own clock). Works on a live or an offline context alike. */
export function scheduleDashboardAlertSound(
  context: BaseAudioContext,
  sound: DashboardAlertSound,
  start: number,
): void {
  const master = context.createGain();
  master.gain.value = MASTER_LEVEL;
  const softener = context.createBiquadFilter();
  softener.type = "lowpass";
  softener.frequency.value = 7500;
  const limiter = context.createDynamicsCompressor();
  // A hard knee: only peaks above the threshold are held back, so a sustained tone is not squeezed quieter than
  // a struck one and every section comes out at a similar loudness.
  limiter.threshold.value = -8;
  limiter.knee.value = 0;
  limiter.ratio.value = 6;
  softener.connect(limiter).connect(master).connect(context.destination);

  const bus = context.createGain();
  bus.connect(softener);

  // A short feedback echo, quieter than the strike, so the last note hangs in the room for a moment.
  const echo = context.createDelay(1);
  echo.delayTime.value = ECHO_SECONDS;
  const feedback = context.createGain();
  feedback.gain.value = ECHO_FEEDBACK;
  const wet = context.createGain();
  wet.gain.value = ECHO_LEVEL;
  bus.connect(echo);
  echo.connect(feedback).connect(echo);
  echo.connect(wet).connect(softener);

  for (const note of SOUNDS[sound]) strike(context, bus, note, start);
}

type AudioContextConstructor = typeof AudioContext;
let sharedContext: AudioContext | null = null;
let lastPlayedAt = 0;

function audioContext(): AudioContext | null {
  if (sharedContext) return sharedContext;
  if (typeof window === "undefined") return null;
  const Constructor: AudioContextConstructor | undefined =
    window.AudioContext ??
    (window as Window & { webkitAudioContext?: AudioContextConstructor }).webkitAudioContext;
  if (!Constructor) return null;
  try {
    sharedContext = new Constructor();
  } catch {
    return null;
  }
  return sharedContext;
}

/**
 * Wakes the audio output. Browsers keep it asleep until someone has touched the page, and Safari only lets a
 * gesture wake it, so this runs on the first tap or key of the session and on the sound controls.
 */
export function unlockDashboardAlertSound(): void {
  const context = audioContext();
  if (context?.state === "suspended") void context.resume().catch(() => {});
}

/**
 * Plays a sound now, or not at all when the browser will not let the page make a sound or another one just
 * played. Never throws. `force` skips the gap, for a preview the person asked for.
 */
export function playDashboardAlertSound(sound: DashboardAlertSound, { force = false } = {}): void {
  const now = Date.now();
  if (!force && now - lastPlayedAt < MIN_GAP_MS) return;
  const context = audioContext();
  if (!context) return;
  lastPlayedAt = now;
  const play = () => {
    try {
      scheduleDashboardAlertSound(context, sound, context.currentTime + 0.02);
    } catch {
      // A browser without part of the Web Audio graph stays silent; the alert on screen is what counts.
    }
  };
  if (context.state === "running") {
    play();
    return;
  }
  context
    .resume()
    .then(() => {
      if (context.state === "running" && Date.now() - now < MAX_START_DELAY_MS) play();
    })
    .catch(() => {});
}
